import { forwardRef } from "react";
import type { AnchorHTMLAttributes } from "react";
import type { ButtonProps as MuiButtonProps } from "@mui/material";
import MuiButton from "@mui/material/Button";
import { Breakpoint, SxProps, Theme, useTheme } from "@mui/material/styles";
import { BUTTON_SIZE_STYLES, ButtonSize } from "src/theme-mui/components/button/button-styles";
import { toSxArray } from "src/utils/mui/sx/sx";

export type ResponsiveButtonSize = ButtonSize | ButtonSize[] | Partial<Record<Breakpoint, ButtonSize>>;

export interface IButtonProps extends Omit<MuiButtonProps, "size">, Pick<AnchorHTMLAttributes<HTMLAnchorElement>, "target" | "rel" | "download"> {
    /**
     * The button size. Accepts a single token or a responsive, mobile-first value:
     * - `"medium"` — fixed size at all breakpoints.
     * - `["small", "medium"]` — `small` from `xs`, `medium` from `sm` up.
     * - `{ xs: "small", md: "large" }` — explicit per-breakpoint sizes.
     *
     * Responsive sizing is resolved with CSS media queries (no hydration flash).
     * Size tokens are inlined (mirroring `ButtonSize`) so tooltips list the values on hover.
     */
    size?:
        | "extraSmall"
        | "small"
        | "medium"
        | "large"
        | ("extraSmall" | "small" | "medium" | "large")[]
        | Partial<Record<Breakpoint, "extraSmall" | "small" | "medium" | "large">>;
}

export type ButtonProps = IButtonProps;

const isResponsiveSize = (size: ResponsiveButtonSize): size is ButtonSize[] | Partial<Record<Breakpoint, ButtonSize>> =>
    Array.isArray(size) || typeof size === "object";

// Normalize an array/object responsive size into ordered [breakpoint, size] entries.
// `breakpointOrder` is the theme's mobile-first key order (`theme.breakpoints.keys`).
const getResponsiveEntries = (
    size: ButtonSize[] | Partial<Record<Breakpoint, ButtonSize>>,
    breakpointOrder: readonly Breakpoint[]
): [Breakpoint, ButtonSize][] => {
    if (Array.isArray(size)) {
        return size
            .map((value, index): [Breakpoint, ButtonSize | undefined] => [breakpointOrder[index], value])
            .filter((entry): entry is [Breakpoint, ButtonSize] => entry[1] != null);
    }

    return breakpointOrder
        .filter(breakpoint => size[breakpoint] != null)
        .map((breakpoint): [Breakpoint, ButtonSize] => [breakpoint, size[breakpoint] as ButtonSize]);
};

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(({ size, sx, ...buttonProps }, ref) => {
    const theme = useTheme();

    if (size == null || !isResponsiveSize(size)) {
        return <MuiButton ref={ref} size={size} sx={sx} {...buttonProps} />;
    }

    const entries = getResponsiveEntries(size, theme.breakpoints.keys);

    if (entries.length === 0) {
        return <MuiButton ref={ref} sx={sx} {...buttonProps} />;
    }

    const [, baseSize] = entries[0];
    const upwardEntries = entries.slice(1);

    // Base size comes from the native `size` prop (correct on SSR/first paint);
    // larger breakpoints are layered on via CSS media queries.
    const responsiveSx: SxProps<Theme> = theme =>
        upwardEntries.reduce<Record<string, unknown>>((styles, [breakpoint, breakpointSize]) => {
            styles[theme.breakpoints.up(breakpoint)] = BUTTON_SIZE_STYLES[breakpointSize];
            return styles;
        }, {});

    return <MuiButton ref={ref} size={baseSize} sx={[responsiveSx, ...toSxArray(sx)]} {...buttonProps} />;
});

Button.displayName = "Button";
